import type {
  ContentComponentConfig,
  ContentRootProps,
  Data,
  ExcursionComponentConfig,
  ExcursionRootProps,
  IMedium,
} from "./component-types";

export interface Citation {
  id: string;
  index: number;
  author: string;
  content: string;
  source: string | null;
  uniqueId: string;
}

export interface Excursion {
  id: string;
  overline: string | null;
  title: string;
  slug: string;
  data: Data<ExcursionComponentConfig, ExcursionRootProps>;
}

export interface ExcursionSmall {
  overline: string | null;
  title: string;
  slug: string;
}

export interface MediumMetadata {
  altText: string;
  caption?: string;
  source: {
    url?: string;
    title?: string;
  };
  isFigure: boolean;
  isExpandable: boolean;
}

export type Figure = IMedium;

export interface PageReadingSettings {
  noContainer?: boolean;
  excursionsInOutline?: boolean;
}

export interface GlossaryItem {
  id: string;
  uniqueId: string;
  term: string;
  definition: string;
}

export interface OutlineItem {
  level: "section" | "subsection" | "subsubsection" | "excursion";
  slug: string;
  content: string;
  isExcursion: boolean;
}

export type Outline = OutlineItem[];

export const languageKeys = [
  "de",
  "en",
  "fr",
  "it",
  "es",
  "pt",
  "ru",
  "zh",
] as const;

export interface PageVersion {
  id: string;
  publishedAt: string;
}

export type LanguageKey = (typeof languageKeys)[number];

export const languageMap: Record<LanguageKey, string> = {
  de: "German",
  en: "English",
  fr: "French",
  it: "Italian",
  es: "Spanish",
  pt: "Portuguese",
  ru: "Russian",
  zh: "Chinese",
};

export interface PageMeta {
  id: string;
  organizationName: string;
  organizationSlug: string;
  projectName: string;
  projectSlug: string;
  languageKey: string;
  topicName: string;
  topicSlug: string;
  name: string;
  slug: string;
  metaTitle: string | null;
  metaDescription: string | null;
  metaImage: string | null;
  analyticsEnabled: boolean;
  indexed: boolean;
  readingSettings: PageReadingSettings;
  publishedAt: Date | null;
  currentVersionId: string | null;
}

export interface PageContent {
  meta: PageMeta;
  content: Data<ContentComponentConfig, ContentRootProps>;
  excursions: Excursion[];
  figures: { content: Figure[]; excursions: Record<string, Figure[]> };
  citations: { content: Citation[]; excursions: Record<string, Citation[]> };
  glossaryItems: GlossaryItem[];
  outline: { content: Outline; excursions: Record<string, Outline> };
  versions: PageVersion[];
  copyrightText?: string | null;
  imprintLink?: string | null;
  privacyLink?: string | null;
  hasImprint?: boolean;
  hasPrivacyPolicy?: boolean;
}
